import { Ref, ref } from "vue";
import { defaultApi } from "@/api";
import { IResponse } from "@/api/Types";
import { NotificationStatus, ReportModule } from "@/global";
import i18n from "@/i18n";
import { DownloadReportResponse } from "@/global/interfaces";
import { useNotificationDrawerStore } from "@/store/NotificationDrawer/NotificationDrawer";
import { getFormattedDate } from "../utils/notificationDrawerUtils";
import { logInfo, logError } from "@/loggers";
import { FEATURE_NAMES } from "@/loggers/types";
import { getStatusIcon, getTagAppearance, getBackgroundColor, getAction, getTag } from "../utils/notificationTagsUtils";

const useDownloadLists = () => {
  const data: Ref<DownloadReportResponse[]> = ref([]);
  const notifications = ref([]);
  const error = ref(null);
  const loading = ref(false);
  const DownloadListsUrl = "inbox";
  const notificationDrawerStore = useNotificationDrawerStore();

  // Pure function to get localized text - will be called fresh each time
  const getLocalizedMessage = (status: string, message?: string): string => {
    // If backend provided a custom message, prefer it
    if (message) return message;

    // Map status to appropriate localized message
    if (status === NotificationStatus.SUCCESSFUL) {
      return i18n.global.t("NOTIFICATION.DOWNLOAD_READY_MESSAGE");
    }

    if (status === NotificationStatus.FAILED) {
      return i18n.global.t("NOTIFICATION.GENERATE_REPORT_FAILED_MESSAGE");
    }

    // For QUEUED and IN_PROGRESS (and any other), use generating message
    return i18n.global.t("NOTIFICATION.GENERATE_REPORT_MESSAGE");
  };

  const getLocalizedFileName = (fileName?: string): string => {
    return fileName || i18n.global.t("EMISSIONS_CONSTANTS.PEER_BENCHMARKING.DETAILED_ANALYSIS.NA");
  };

  const fetchData = async (): Promise<DownloadReportResponse[]> => {
    loading.value = true;
    try {
      const response: IResponse = await defaultApi.apiUnversioned.downloadLists.getAll(DownloadListsUrl);
      data.value = response.data as DownloadReportResponse[];

      notifications.value = mapNotificationData(response.data);
      logInfo(FEATURE_NAMES.MVS_RISK_MANAGEMENT, "Get all Notifications" + JSON.stringify(notifications.value));
      return notifications.value;
    } catch (err) {
      logError(FEATURE_NAMES.MVS_RISK_MANAGEMENT, "Error" + err);
      error.value = err;
    } finally {
      loading.value = false;
    }
  };

  const mapNotificationData = (data: DownloadReportResponse[]): DownloadReportResponse[] => {
    return data.map((item: any) => ({
      status: NotificationStatus[item.status] || NotificationStatus.IN_PROGRESS,
      icon: "tray-arrow-down",
      message: getLocalizedMessage(item.status, item.message),
      file: getLocalizedFileName(item.fileName),
      statusIcon: getStatusIcon(item.status),
      productName: item.productName,
      createdAt: getFormattedDate(item.createdAt, "d MMM yyyy HH:mm", false),
      metaIcon: "module-2",
      jobId: item.jobId,
      fileSize: item.fileSize,
      isDownloadAcknowledged: item.isDownloadAcknowledged,
      tagAppearance: getTagAppearance(item.status),
      backgroundColor: getBackgroundColor(item.status),
      action: getAction(item.status, item.downloadUrl, ReportModule.DOWNLOAD),
      tag: getTag(item.status, item.expiryTime)
    }));
  };

  // Method to refresh notifications with current locale
  const refreshLocalizedNotifications = (): void => {
    if (data.value.length > 0) {
      notifications.value = mapNotificationData(data.value);
    }
  };

  const deleteData = async (report: DownloadReportResponse) => {
    loading.value = true;
    try {
      const jobIdToDelete = report?.jobId;
      if (jobIdToDelete) {
        const deleteUrl = `${jobIdToDelete}`;
        await defaultApi.apiUnversioned.downloadLists.deleteReport(deleteUrl);
      }

      notificationDrawerStore.filterNotification(jobIdToDelete);
      logInfo(FEATURE_NAMES.MVS_RISK_MANAGEMENT, "Deleted Notification from list, Deleted jobId = " + jobIdToDelete);
    } catch (err) {
      logError(
        FEATURE_NAMES.MVS_RISK_MANAGEMENT,
        "Error while Deleting Notification from list: " + (err instanceof Error ? err.message : String(err))
      );
      error.value = err;
    } finally {
      loading.value = false;
    }
  };
  const acknowledgeReportDownload = async () => {
    loading.value = true;
    try {
      const acknowlegeDownloadReportUrl = "acknowledge";
      await defaultApi.apiUnversioned.downloadLists.patchReport(acknowlegeDownloadReportUrl);
      logInfo(FEATURE_NAMES.MVS_RISK_MANAGEMENT, "All Notifications are acknowledged");
    } catch (err) {
      logError(FEATURE_NAMES.MVS_RISK_MANAGEMENT, "Error while acknowledging notification" + err);
      error.value = err;
    } finally {
      loading.value = false;
    }
  };

  return {
    data,
    error,
    loading,
    fetchData,
    notifications,
    refreshLocalizedNotifications,
    deleteData,
    acknowledgeReportDownload
  };
};

export default useDownloadLists;
